import { useMemo } from "react";
import { NavLink } from "react-router-dom";
import { Trash2, AlertTriangle, Clock } from "lucide-react";
import { Button } from "./ui/button";
import {
  cancelBookingIsAvailable,
  isSameDayCancellationAvailable,
  isSameDayCancellationAllowedForPhone,
} from "@/utils/booking";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { ClassesTableProps } from "../@types/props.types";
import { useAuth } from "@/contexts/AuthContext";
import { formatDate, formatTime, formatCancellationDeadline } from "@/utils/dateFormatters";

type Appointment = ClassesTableProps["appointments"][number];

type CancellationCellProps = {
  classItem: Appointment;
  sameDayAllowed: boolean;
  isSameDayCancellationsLoading: boolean;
  hasPendingCancellation: boolean;
  onClickDeleteClasses: (appointmentId: string) => void;
  onClickSameDayCancel: (appointmentId: string) => void;
  onDeleteSameDayCancel: (appointmentId: string) => void;
};

const CancellationCell = ({
  classItem,
  sameDayAllowed,
  isSameDayCancellationsLoading,
  hasPendingCancellation,
  onClickDeleteClasses,
  onClickSameDayCancel,
  onDeleteSameDayCancel,
}: CancellationCellProps) => {
  if (cancelBookingIsAvailable(classItem.start_date)) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="h-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
        onClick={() => onClickDeleteClasses(classItem.appointment_id)}
      >
        <Trash2 className="mr-2 h-4 w-4" />
        Отменить
      </Button>
    );
  }

  if (!sameDayAllowed) {
    return (
      <div className="text-center text-sm text-muted-foreground">
        Отмена этого занятия недоступна после 18:00 -{" "}
        {formatCancellationDeadline(classItem.start_date)}
      </div>
    );
  }

  if (isSameDayCancellationsLoading) {
    return null;
  }

  if (hasPendingCancellation) {
    return (
      <div className="flex flex-col items-center gap-1">
        <div className="flex items-center gap-1.5 text-center text-sm text-amber-600">
          <Clock className="h-4 w-4" />
          Запрос на отмену отправлен
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-muted-foreground hover:text-destructive"
          onClick={() => onDeleteSameDayCancel(classItem.appointment_id)}
        >
          <Trash2 className="mr-1 h-3 w-3" />
          Отменить запрос
        </Button>
      </div>
    );
  }

  if (isSameDayCancellationAvailable(classItem.start_date)) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="h-8 text-amber-600 hover:bg-amber-50 hover:text-amber-700"
        onClick={() => onClickSameDayCancel(classItem.appointment_id)}
      >
        <AlertTriangle className="mr-2 h-4 w-4" />
        Отмена день в день до 10:00
      </Button>
    );
  }

  return (
    <div className="text-center text-sm text-muted-foreground">
      Отмена этого занятия недоступна после 18:00 -{" "}
      {formatCancellationDeadline(classItem.start_date)}
    </div>
  );
};

const ClassesTable = ({
  appointments,
  clubNames,
  onClickDeleteClasses,
  onClickSameDayCancel,
  onDeleteSameDayCancel,
  sameDayCancellations,
  isSameDayCancellationsLoading,
  userName,
}: ClassesTableProps) => {
  const { authPhone } = useAuth();
  const sameDayAllowed = isSameDayCancellationAllowedForPhone(authPhone);
  const cancelledExerciseIds = useMemo(
    () => new Set(sameDayCancellations.map((c) => c.exercise_id)),
    [sameDayCancellations]
  );
  if (appointments.length === 0) {
    return (
      <div className="flex h-full items-center justify-center rounded-lg border p-6">
        <span className="text-center text-sm text-gray-500">
          Активных занятий нет. Вы можете записаться на занятие в разделе{" "}
          <NavLink to="/sign-up" className="text-primary hover:underline">
            Расписание
          </NavLink>
        </span>
      </div>
    );
  }

  return (
    <div className="mb-6 w-full">
      <div className="overflow-hidden rounded-lg border bg-white shadow-sm">
        <div className="border-b">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50/75 hover:bg-transparent">
                <TableHead className="w-[25%] px-2 py-3 text-center font-medium text-gray-600">
                  Дата
                </TableHead>
                <TableHead className="w-[25%] px-2 py-3 text-center font-medium text-gray-600">
                  Тренер / ребенок
                </TableHead>
                <TableHead className="w-[50%] px-2 py-3 text-center font-medium text-gray-600">
                  Филиал
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {appointments.map((classItem) => {
                const isGroup = classItem.type === "classes";
                return (
                  <TableRow
                    key={classItem.appointment_id}
                    className="transition-colors hover:bg-gray-50/75"
                  >
                    <TableCell className="px-2 py-3">
                      <div
                        className={`mx-auto mb-1 inline-block w-fit rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${
                          isGroup ? "bg-primary/10 text-primary" : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {isGroup ? "Группа" : "Персональное"}
                      </div>
                      <div className="text-center text-sm font-medium">
                        {formatDate(classItem.start_date)}
                      </div>
                      <div className="text-center text-xs text-muted-foreground">
                        {formatTime(classItem.start_date)}
                      </div>
                      {isGroup && classItem.service?.title && (
                        <div className="mt-1 text-center text-[11px] text-muted-foreground">
                          {classItem.service.title}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="px-2 py-3 text-center text-sm">
                      {classItem.employee?.name + " " + "/" + " " + userName}
                    </TableCell>
                    <TableCell className="px-2 py-3">
                      <div className="flex flex-col items-center gap-2">
                        <span className="text-center text-sm">{clubNames[classItem.club_id]}</span>
                        <CancellationCell
                          classItem={classItem}
                          sameDayAllowed={sameDayAllowed}
                          isSameDayCancellationsLoading={isSameDayCancellationsLoading}
                          hasPendingCancellation={cancelledExerciseIds.has(
                            classItem.appointment_id
                          )}
                          onClickDeleteClasses={onClickDeleteClasses}
                          onClickSameDayCancel={onClickSameDayCancel}
                          onDeleteSameDayCancel={onDeleteSameDayCancel}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default ClassesTable;
